import apiClient from '@/lib/axios';
import { useMutation, useQuery } from '@tanstack/react-query';

/**
 * Session API response
 */
interface SessionResponse {
  success: boolean;
  data: {
    sessionId: string;
    orderId: string;
    token: string;
    branchId: string;
    expiresAt: string;
  };
}

/**
 * Branch validation response
 */
interface BranchValidationResponse {
  success: boolean;
  data: {
    branchId: string;
    name: string;
    isActive: boolean;
  };
}

/**
 * Create public session mutation (no auth required)
 */
export function useCreateSession() {
  return useMutation({
    mutationFn: async (params: {
      branchId: string;
      tableNumber?: string;
      orderType?: 'dine-in' | 'takeaway';
    }) => {
      const response = await apiClient.post<SessionResponse>(
        '/api/v1/public/sessions',
        params
      );
      return response.data.data;
    },
  });
}

/**
 * Validate branch query (no auth required)
 */
export function useValidateBranch(branchId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['branch-validation', branchId],
    queryFn: async () => {
      if (!branchId) {
        throw new Error('Branch ID is required');
      }
      const response = await apiClient.get<BranchValidationResponse>(
        `/api/v1/public/branches/${branchId}/validate`
      );
      return response.data.data;
    },
    enabled: enabled && !!branchId,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
